import { useState } from 'react';
import { useFeedback } from '../../hooks/useApi';
import styles from './FeedbackPage.module.scss';

type FeedbackType = 'BUG' | 'FEATURE_REQUEST';

const TYPE_LABELS: Record<FeedbackType, string> = {
    BUG: 'Bug Report',
    FEATURE_REQUEST: 'Feature Request',
};

export function FeedbackPage() {
    const { submitFeedback } = useFeedback();
    const [type, setType] = useState<FeedbackType>('BUG');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            await submitFeedback({ type, title: title.trim(), description: description.trim() });
            setSubmitted(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to submit feedback');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReset = () => {
        setType('BUG');
        setTitle('');
        setDescription('');
        setSubmitted(false);
        setError(null);
    };

    if (submitted) {
        return (
            <div className={styles.page}>
                <div className={styles.successCard}>
                    <div className={styles.successIcon}>✓</div>
                    <h2 className={styles.successTitle}>Thank you for your feedback!</h2>
                    <p className={styles.successText}>
                        Your submission has been received and will be reviewed by our team. We read every report and use
                        them to improve Expat Horizon.
                    </p>
                    <button className={styles.resetButton} onClick={handleReset}>
                        Submit another
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <h1 className={styles.title}>Feedback</h1>
                <p className={styles.subtitle}>Found a bug or have an idea? We want to hear from you.</p>
            </div>

            <div className={styles.formCard}>
                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className={styles.typeToggle}>
                        {(['BUG', 'FEATURE_REQUEST'] as FeedbackType[]).map((t) => (
                            <button
                                key={t}
                                type="button"
                                className={`${styles.typeButton} ${type === t ? styles.typeActive : ''} ${t === 'BUG' ? styles.typeBug : styles.typeFeature}`}
                                onClick={() => setType(t)}
                            >
                                <span className={styles.typeIcon}>{t === 'BUG' ? '🐛' : '💡'}</span>
                                {TYPE_LABELS[t]}
                            </button>
                        ))}
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="feedback-title">
                            {type === 'BUG' ? 'What went wrong?' : 'What would you like to see?'}
                        </label>
                        <input
                            id="feedback-title"
                            type="text"
                            className={styles.input}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={
                                type === 'BUG'
                                    ? 'e.g., Simulator crashes when I change the retirement age'
                                    : 'e.g., Show NPS balance in the Indian assets section'
                            }
                            maxLength={200}
                            required
                        />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="feedback-description">
                            {type === 'BUG'
                                ? 'Steps to reproduce / more detail'
                                : 'Describe the feature in more detail'}
                        </label>
                        <textarea
                            id="feedback-description"
                            className={`${styles.input} ${styles.textarea}`}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={
                                type === 'BUG'
                                    ? 'What did you do? What did you expect to happen? What actually happened?'
                                    : 'Why would this be useful? How should it work?'
                            }
                            rows={6}
                            maxLength={2000}
                            required
                        />
                        <span className={styles.charCount}>{description.length} / 2000</span>
                    </div>

                    {error && <p className={styles.errorText}>{error}</p>}

                    <div className={styles.actions}>
                        <button type="submit" className={styles.submitButton} disabled={submitting}>
                            {submitting ? 'Submitting...' : `Submit ${TYPE_LABELS[type]}`}
                        </button>
                    </div>
                </form>
            </div>

            <div className={styles.note}>
                <p>
                    Submissions are reviewed daily. For urgent issues, please email{' '}
                    <a href="mailto:avtechaillc@gmail.com" className={styles.noteLink}>
                        avtechaillc@gmail.com
                    </a>{' '}
                    directly.
                </p>
            </div>
        </div>
    );
}
